'use client';
import React from 'react';
import { Button } from '@/components/ui/button';
import { WifiOff } from 'lucide-react';
import { captureException } from '@/modules/analytics';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  isOffline: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  eventId: string | null;
}

// Ensure React.Component is available before extending
const ReactComponent = React.Component;

export class GlobalErrorBoundary extends ReactComponent<Props, State> {
  private resetTimeoutId?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isOffline: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      isOffline: typeof navigator !== 'undefined' && !navigator.onLine,
      error,
      eventId: Math.random().toString(36).substring(2, 15),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Check if it's a video-related error we should ignore
    if (
      error.message &&
      (error.message.includes('videoElement.play') ||
        error.message.includes('play is not a function') ||
        error.message.includes('video track') ||
        error.message.includes('this.videoElement'))
    ) {
      console.warn('Video track error ignored:', error.message);
      // Reset the error state to continue normally
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        eventId: null,
      });
      return;
    }

    // Continue with existing error handling for non-video errors
    this.setState({ errorInfo });

    // Only log in browser environment
    if (typeof window !== 'undefined') {
      console.error('GlobalErrorBoundary caught a critical error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        eventId: this.state.eventId,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });

      // Log to external service only in browser
      this.logToExternalService(error, errorInfo);

      // Report the render crash to PostHog (guarded no-op when disabled/uninitialised)
      captureException(error, {
        exceptionSource: 'react-error-boundary',
        errorId: this.state.eventId,
        componentStack: errorInfo.componentStack,
        isOffline: typeof navigator !== 'undefined' && !navigator.onLine,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
      });

      // Auto-retry for network errors
      if (this.shouldAutoRetry(error)) {
        this.scheduleAutoRetry();
      }
    }
  }

  private shouldAutoRetry(error: Error): boolean {
    const retryableErrors = [
      'NetworkError',
      'ChunkLoadError',
      'Loading chunk',
      'Loading CSS chunk',
      'failed to fetch',
      'timeout',
    ];
    return retryableErrors.some((retryableError) =>
      error.message.toLowerCase().includes(retryableError.toLowerCase())
    );
  }

  private scheduleAutoRetry() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
    this.resetTimeoutId = setTimeout(() => {
      this.handleRetry();
    }, 10000);
  }

  private async logToExternalService(error: Error, errorInfo: React.ErrorInfo) {
    try {
      // Only run in browser with proper checks
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const [{ logError }, { getSession }] = await Promise.all([
          import('@/firebase/firebase'),
          import('next-auth/react'),
        ]);

        const sessionData = await getSession();

        if (sessionData?.user?.id) {
          await logError(sessionData.user.id, error);
        }
      }
    } catch (logError) {
      console.error('Failed to log error to external service:', logError);
    }
  }

  handleRetry = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
    this.setState({
      hasError: false,
      isOffline: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  render() {
    if (this.state.hasError && this.state.isOffline) {
      return (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black text-white">
          <WifiOff className="w-12 h-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Internet Connection</h2>
          <p className="text-gray-400 text-sm">Please reconnect and reload.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-white/10 rounded-xl hover:bg-white/20 text-white text-sm"
          >
            Reload
          </button>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-950 to-slate-900 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-gray-800 rounded-xl shadow-2xl p-8 text-center">
            <div className="mb-6">
              <svg
                className="w-20 h-20 mx-auto text-red-500 mb-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <h1 className="text-2xl font-bold text-white mb-3">
                Oops! Something went wrong
              </h1>
              <p className="text-gray-300 mb-4">
                We encountered an unexpected error. Our team has been notified
                and is working on fixing it.
              </p>
              {this.state.eventId && (
                <div className="bg-gray-700 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-400">Error ID</p>
                  <p className="text-sm font-mono text-white">
                    {this.state.eventId}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Please include this ID when reporting the issue
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <Button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3"
              >
                Try Again
              </Button>
              <Button
                onClick={this.handleReload}
                variant="outline"
                className="w-full border-gray-600 text-gray-300 hover:bg-gray-700 font-semibold py-3"
              >
                Reload Page
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="ghost"
                className="w-full text-gray-400 hover:text-white hover:bg-gray-700 font-semibold py-3"
              >
                Go to Home
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-300 mb-2">
                  Technical Details (Development Mode)
                </summary>
                <div className="bg-gray-900 rounded-lg p-4 text-xs">
                  <div className="text-red-400 font-bold mb-2">Error:</div>
                  <pre className="text-red-300 mb-4 overflow-auto">
                    {this.state.error.message}
                  </pre>
                  <div className="text-red-400 font-bold mb-2">
                    Stack Trace:
                  </div>
                  <pre className="text-red-300 mb-4 overflow-auto max-h-32">
                    {this.state.error.stack}
                  </pre>
                  {this.state.errorInfo?.componentStack && (
                    <>
                      <div className="text-red-400 font-bold mb-2">
                        Component Stack:
                      </div>
                      <pre className="text-red-300 overflow-auto max-h-32">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}

            <div className="mt-6 text-xs text-gray-500">
              <p>
                If the problem persists, please contact support at{' '}
                <span className="text-blue-400">support@verchool.com</span>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
